/**
 * Alert 메시지 다국어 지원
 *
 * 사용법:
 * import { getAlertMessage } from '@src/shared/constants/alertMessages';
 * const message = getAlertMessage('EMAIL_ALREADY_IN_USE');
 * const messageWithParams = getAlertMessage('TEXT_LENGTH_EXCEEDED', { maxLength: 100 });
 */

import type { LocalizedMessages } from '../dto/EntityRepository';
import { useLanguageStore, Language } from '../store/languageStore';

export type { Language } from '../store/languageStore';

export type AlertMessageKey =
  // Alert Store
  | 'UNSAVED_CHANGES'
  | 'TEXT_LENGTH_EXCEEDED'
  | 'SUBSCRIPTION_FAILED'
  
  // Global Error
  | 'SESSION_EXPIRED'
  | 'NO_MATCHING_MEMBER'
  | 'AUTHENTICATION_FAILED'
  | 'DATABASE_CONNECTION_ERROR'
  | 'IMAGE_UPLOAD_FAILED'
  | 'VALUE_ALREADY_IN_USE'
  | 'MISSING_REQUIRED_FIELD'
  | 'INVALID_VALUE_REQUESTED'
  | 'FILE_TOO_BIG'
  | 'NETWORK_ERROR'
  | 'SERVER_NOT_AVAILABLE'
  | 'EMAIL_SEND_FAILED'
  | 'EMAIL_NOT_VALID'
  | 'EMAIL_VERIFICATION_ERROR'
  | 'INVALID_CODE'
  | 'CODE_EXPIRED'
  | 'SHORTEN_TEXT'
  | 'WAITLIST_WAITING'
  | 'INVALID_PASSWORD'
  | 'GROUP_MEMBER_LINK_MEMBER_NOT_FOUND'
  | 'GROUP_MEMBER_LINK_GROUP_ACCOUNT'
  | 'GROUP_MEMBER_LINK_ALREADY_LINKED'
  | 'GROUP_MEMBER_LINK_PASSWORD_NOT_SET'
  | 'GROUP_MEMBER_LINK_TOKEN_EXPIRED'
  | 'GROUP_MEMBER_LINK_TOKEN_INVALID'
  | 'GROUP_MEMBER_LINK_TARGET_MISMATCH'
  | 'GROUP_MEMBER_LINK_PROFILE_CHANGED'
  | 'STORAGE_FULL_FREE'
  | 'STORAGE_FULL_ACTIVE'
  | 'SOMETHING_WENT_WRONG'
  
  // Other
  | 'ALL_CONTENT_PRIVATE'
  | 'SCHEDULED_START_BEFORE_END'
  // Confirm / UI alerts
  | 'CONFIRM_DELETE_TEXT'
  | 'QR_CODES_PUBLIC_ONLY'
  | 'SEO_REQUIRES_SEARCHABLE_ARCHIVE'
  | 'EXIT_EDIT_MODE'
  | 'START_YEAR_GREATER_THAN_END'
  | 'END_YEAR_SMALLER_THAN_START'
  | 'CONFIRM_RETURN_HOME'
  | 'COMPLETE_THIS_STEP'
  | 'CONFIRM_CHANGE_VISIBILITY'
  | 'CONFIRM_DELETE_WORK'
  | 'CONFIRM_DELETE_GROUP_MEMBER'
  | 'CONFIRM_UNLINK_GROUP_MEMBER'
  | 'CONFIRM_DELETE_WORKS_BULK'
  | 'CONFIRM_DELETE_VENUES_BULK'
  | 'CONFIRM_DELETE_TEXTS_BULK'
  | 'CONFIRM_DELETE_DETAIL_CUT'
  | 'CONFIRM_DELETE_TEXT_BLOCK'
  | 'TEXTBLOCK_IMAGE_MAX_EXCEEDED'
  | 'CONFIRM_DELETE_PROJECT'
  | 'CONFIRM_MAKE_PROJECT_PUBLIC'
  | 'CONFIRM_MAKE_PROJECT_PRIVATE'
  | 'INVALID_YEAR_FORMAT'
  | 'INVALID_MONTH_FORMAT'
  | 'INVALID_DAY_FORMAT'
  | 'FILE_TOO_BIG_PREVIEW'
  | 'UNSUPPORTED_IMAGE_FORMAT'
  | 'FAILED_TO_LOAD_IMAGE'
  | 'BULK_UPLOAD_IMAGE_MAX_EXCEEDED'
  | 'BULK_IMPORT_MAX_EXCEEDED'
  | 'SELECT_TO_IMPORT_EMPTY'
  | 'WORK_LIST_EXPORT_MAX_ITEMS'
  | 'SELECT_TO_WORKS_EMPTY'
  | 'FORMATION_DISPLAY_SIZE_NOT_AVAILABLE'
  | 'ADD_WORK_BEFORE_FLOOR_PLAN'
  | 'ONLY_NUMBERS_ALLOWED'
  | 'INVALID_SIZE_FORMAT'
  | 'SET_MAIN_SIZE_UNIT_REQUIRES_SIZE'
  | 'SET_MAIN_SIZE_UNIT_REQUIRES_SIZE_FOR_ALL'
  | 'INVALID_WORK_TYPE'
  | 'COUNTRY_NOT_SUPPORTED'
  | 'INVALID_PRICE_FORMAT'
  | 'USER_NOT_FOUND'
  | 'INVALID_EMAIL_FORMAT'
  | 'CONFIRM_DELETE_CAREER'
  | 'THANK_YOU_PARTICIPATION'
  | 'SUBSCRIBE_USE_DESKTOP'
  | 'CONFIRM_CHANGE_LINK_ID'
  | 'CONFIRM_LOGOUT_UNSAVED'
  | 'CONFIRM_LOGOUT'
  | 'CHECK_FORMAT'
  | 'PREVIEW_MODE_VERIFY_EMAIL'
  | 'CONFIRM_GO_INSTAGRAM'
  | 'CONFIRM_DELETE_SUPPORT_MESSAGE'
  | 'CONFIRM_DELETE_MATERIAL_ALIAS'
  | 'CONFIRM_DELETE_VENUE'
  | 'SUPPORT_TYPE_REQUIRED'
  | 'SUPPORT_AREA_REQUIRED'
  | 'SUPPORT_CONTENT_REQUIRED'
  | 'CONFIRM_MAKE_PAGE_PUBLIC'
  | 'CONFIRM_MAKE_PAGE_SEARCHABLE'
  | 'CONFIRM_MAKE_PAGE_PRIVATE'
  | 'VIDEO_URL_NULL'
  | 'CONFIRM_MOVE_TO_VIDEO_URL_PLATFORM'
  | 'CONFIRM_MOVE_TO_PDF'
  | 'CONFIRM_MOVE_TO_CAREER_RELATED_URL'
  | 'CONFIRM_MOVE_TO_TEXT_BLOCK_LINK'
  | 'INVALID_URL_FORMAT'
  | 'INVALID_VIDEO_URL_FORMAT'
  | 'INVALID_PROJECT_INFO_URL_FORMAT'
  | 'INVALID_TEXT_BLOCK_LINK_URL_FORMAT'
  | 'CONFIRM_DELETE_TAG'
  | 'CONFIRM_DELETE_VENUE_TAG'
  // Password validation (useValidation explainPasswordIssues)
  | 'PASSWORD_LENGTH_REQUIRED'
  | 'PASSWORD_NEED_LOWERCASE'
  | 'PASSWORD_NEED_UPPERCASE'
  | 'PASSWORD_NEED_NUMBER'
  | 'PASSWORD_NEED_SPECIAL'
  | 'PASSWORD_NO_SPACES';

const alertMessages: LocalizedMessages<AlertMessageKey> = {
  // Alert Store
  UNSAVED_CHANGES: {
    [Language.EN]:
      "Changes are not saved.\nContinuing will discard them.\n\nProceed?",
    [Language.KR]:
      "저장되지 않은 변경사항이 있습니다.\n이대로 진행하면 변경 내용이 사라집니다.\n\n진행하겠습니까?",
  },
  TEXT_LENGTH_EXCEEDED: {
    [Language.EN]: "Maximum length is {maxLength} characters. Try again.",
    [Language.KR]: "최대 길이는 {maxLength}자입니다. 다시 시도해주세요.",
  },
  SUBSCRIPTION_FAILED: {
    [Language.EN]: "Subscription failed. Please try again.",
    [Language.KR]: "구독에 실패했습니다. 다시 시도해주세요.",
  },
  // Global Error
  SESSION_EXPIRED: {
    [Language.EN]: "Session expired. Sign in again.",
    [Language.KR]: "세션이 만료되었습니다. 다시 로그인해주세요.",
  },
  NO_MATCHING_MEMBER: {
    [Language.EN]: "No matching Member. Please check again.",
    [Language.KR]: "일치하는 회원이 없습니다. 다시 확인해주세요.",
  },
  AUTHENTICATION_FAILED: {
    [Language.EN]: "Authentication failed.",
    [Language.KR]: "인증에 실패했습니다.",
  },
  DATABASE_CONNECTION_ERROR: {
    [Language.EN]: "Database Connection Error.",
    [Language.KR]: "내부 연결 오류가 발생했습니다.",
  },
  IMAGE_UPLOAD_FAILED: {
    [Language.EN]: "Image upload failed. Retry.",
    [Language.KR]: "이미지 업로드에 실패했습니다. 다시 시도해주세요.",
  },
  VALUE_ALREADY_IN_USE: {
    [Language.EN]: "This value is already in use.",
    [Language.KR]: "이미 사용 중인 값입니다.",
  },
  MISSING_REQUIRED_FIELD: {
    [Language.EN]: "Missing required field.",
    [Language.KR]: "필수 값이 누락되었습니다.",
  },
  INVALID_VALUE_REQUESTED: {
    [Language.EN]: "Invalid value requested.",
    [Language.KR]: "잘못된 값이 요청되었습니다.",
  },
  FILE_TOO_BIG: {
    [Language.EN]: "File is too big. MAX is {maxSize}",
    [Language.KR]: "파일이 너무 큽니다. 최대 크기는 {maxSize}입니다.",
  },
  NETWORK_ERROR: {
    [Language.EN]: "Network error. Please check your internet and try again.",
    [Language.KR]: "네트워크 오류가 발생했습니다. 인터넷 연결을 확인하고 다시 시도해주세요.",
  },
  SERVER_NOT_AVAILABLE: {
    [Language.EN]: "Server is not available. Try again later.",
    [Language.KR]: "서버를 사용할 수 없습니다. 나중에 다시 시도해주세요.",
  },
  EMAIL_SEND_FAILED: {
    [Language.EN]: "Failed to send email.",
    [Language.KR]: "이메일 전송에 실패했습니다.",
  },
  EMAIL_NOT_VALID: {
    [Language.EN]: "Email address is not valid.",
    [Language.KR]: "유효하지 않은 이메일 주소입니다.",
  },
  EMAIL_VERIFICATION_ERROR: {
    [Language.EN]: "Email Verification Error.",
    [Language.KR]: "이메일 인증 오류가 발생했습니다.",
  },
  INVALID_CODE: {
    [Language.EN]: "Invalid code.",
    [Language.KR]: "인증 코드가 올바르지 않습니다.",
  },
  CODE_EXPIRED: {
    [Language.EN]: "Code expired. Please resend.",
    [Language.KR]: "인증 코드가 만료되었습니다. 다시 전송해주세요.",
  },
  SHORTEN_TEXT: {
    [Language.EN]: "Please shorten the text.",
    [Language.KR]: "텍스트를 줄여주세요.",
  },
  WAITLIST_WAITING: {
    [Language.EN]: "Thanks for your patience. We'll be in touch when it's time.",
    [Language.KR]: "기다려주셔서 감사합니다. 준비되면 연락드리겠습니다.",
  },
  INVALID_PASSWORD: {
    [Language.EN]: "Invalid password.",
    [Language.KR]: "잘못된 비밀번호입니다.",
  },
  GROUP_MEMBER_LINK_MEMBER_NOT_FOUND: {
    [Language.EN]: "No user was found with this email.",
    [Language.KR]: "해당 이메일의 사용자를 찾을 수 없습니다.",
  },
  GROUP_MEMBER_LINK_GROUP_ACCOUNT: {
    [Language.EN]: "Group accounts cannot be linked.",
    [Language.KR]: "그룹 계정은 연결할 수 없습니다.",
  },
  GROUP_MEMBER_LINK_ALREADY_LINKED: {
    [Language.EN]: "This account is already linked to another member in this group.",
    [Language.KR]: "이 계정은 현재 그룹의 다른 멤버에 이미 연결되어 있습니다.",
  },
  GROUP_MEMBER_LINK_PASSWORD_NOT_SET: {
    [Language.EN]: "Accounts without a password cannot be linked.",
    [Language.KR]: "비밀번호가 설정되지 않은 계정은 연결할 수 없습니다.",
  },
  GROUP_MEMBER_LINK_TOKEN_EXPIRED: {
    [Language.EN]: "The connection information has expired. Please authenticate again.",
    [Language.KR]: "연결 정보가 만료되었습니다. 다시 인증해주세요.",
  },
  GROUP_MEMBER_LINK_TOKEN_INVALID: {
    [Language.EN]: "The connection information is invalid. Please authenticate again.",
    [Language.KR]: "연결 정보가 올바르지 않습니다. 다시 인증해주세요.",
  },
  GROUP_MEMBER_LINK_TARGET_MISMATCH: {
    [Language.EN]: "The connection information does not match this group member. Please authenticate again.",
    [Language.KR]: "현재 그룹 멤버와 연결 정보가 일치하지 않습니다. 다시 인증해주세요.",
  },
  GROUP_MEMBER_LINK_PROFILE_CHANGED: {
    [Language.EN]: "The individual profile has changed. Please authenticate again to review the latest information.",
    [Language.KR]: "개인 계정의 프로필이 변경되었습니다. 다시 인증해 최신 정보를 확인해주세요.",
  },
  STORAGE_FULL_FREE: {
    [Language.EN]: "Storage full. Subscribe to get more space.",
    [Language.KR]: "저장 공간이 모두 사용되었습니다. 구독으로 10GB까지 확장할 수 있습니다.",
  },
  STORAGE_FULL_ACTIVE: {
    [Language.EN]: "Storage full. Upgrade your plan to get more space.",
    [Language.KR]: "저장 공간이 모두 사용되었습니다. 플랜 업그레이드로 한도를 늘리세요.",
  },
  SOMETHING_WENT_WRONG: {
    [Language.EN]: "Something went wrong. Please try again later.",
    [Language.KR]: "문제가 발생했습니다. 나중에 다시 시도해주세요.",
  },
  
  // Other
  ALL_CONTENT_PRIVATE: {
    [Language.EN]: "All content on this page is private.",
    [Language.KR]: "해당 Archive의 모든 콘텐츠는 비공개입니다.",
  },
  SCHEDULED_START_BEFORE_END: {
    [Language.EN]: "Scheduled start time must be before scheduled end time.",
    [Language.KR]: "시작 시간은 종료 시간보다 이전이어야 합니다.",
  },
  CONFIRM_DELETE_TEXT: {
    [Language.EN]: "Are you sure you want to delete this text?\nThe related data (text blocks, project elements) will also be deleted.\n\nThis action cannot be undone.",
    [Language.KR]: "해당 Text를 삭제하시겠습니까?\n관련 데이터(Text Block, Project Element)도 삭제됩니다.\n\n이 작업은 취소할 수 없습니다.",
  },
  QR_CODES_PUBLIC_ONLY: {
    [Language.EN]: "QR codes are available only for public archives.",
    [Language.KR]: "QR Code는 Public 상태에서만 사용할 수 있습니다.",
  },
  SEO_REQUIRES_SEARCHABLE_ARCHIVE: {
    [Language.EN]: "Share and Search require Searchable or Public Page Visibility.",
    [Language.KR]: "공유와 검색엔진 설정은\n페이지 공개 설정이 Searchable 또는 Public일 때 사용할 수 있습니다.",
  },
  EXIT_EDIT_MODE: {
    [Language.EN]: "Unavailable in edit mode.\nWould you like to switch to view mode?",
    [Language.KR]: "편집 모드에서는 사용할 수 없습니다.\n보기 모드로 전환하겠습니까?",
  },
  // EXIT_EDIT_MODE: {
  //   [Language.EN]: "Exit edit mode.",
  //   [Language.KR]: "보기 모드로 전환하세요.",
  // },
  START_YEAR_GREATER_THAN_END: {
    [Language.EN]: "Start year cannot be greater than end year",
    [Language.KR]: "시작 연도는 종료 연도보다 클 수 없습니다.",
  },
  END_YEAR_SMALLER_THAN_START: {
    [Language.EN]: "End year cannot be smaller than start year",
    [Language.KR]: "종료 연도는 시작 연도보다 작을 수 없습니다.",
  },
  CONFIRM_RETURN_HOME: {
    [Language.EN]: "Returning to Home.\nCurrent data won't be saved. Continue?",
    [Language.KR]: "Home으로 돌아갑니다.\n현재 데이터가 저장되지 않습니다. 계속하시겠습니까?",
  },
  COMPLETE_THIS_STEP: {
    [Language.EN]: "You need to complete this step.",
    [Language.KR]: "이 단계를 완료해주세요.",
  },
  CONFIRM_CHANGE_VISIBILITY: {
    [Language.EN]: "Do you want to change the visibility?",
    [Language.KR]: "공개 여부를 변경하시겠습니까?",
  },
  CONFIRM_DELETE_WORK: {
    [Language.EN]: "Are you sure you want to delete this work?\nThe related data (detail cuts, project elements) will also be deleted.\n\nThis action cannot be undone.",
    [Language.KR]: "해당 Work를 삭제하시겠습니까?\n관련 데이터(Detail Cut, Project Element)도 삭제됩니다.\n\n이 작업은 취소할 수 없습니다.",
  },
  CONFIRM_DELETE_GROUP_MEMBER: {
    [Language.EN]: "Are you sure you want to delete this group member?\n\nThis action cannot be undone.",
    [Language.KR]: "해당 그룹 멤버를 삭제하시겠습니까?\n\n이 작업은 취소할 수 없습니다.",
  },
  CONFIRM_UNLINK_GROUP_MEMBER: {
    [Language.EN]: "Disconnect this account?\nThe copied profile and related content will remain.",
    [Language.KR]: "계정 연결을 해제하시겠습니까?\n복사된 프로필과 관련 콘텐츠는 그대로 유지됩니다.",
  },
  CONFIRM_DELETE_TAG: {
    [Language.EN]: "Delete label \"{name}\"?\nLinks from works to this label will be removed (works are kept).",
    [Language.KR]: "라벨 \"{name}\"을(를) 삭제할까요?\nWork에 붙은 라벨만 제거되고 Work는 유지됩니다.",
  },
  CONFIRM_DELETE_VENUE_TAG: {
    [Language.EN]: "Delete label \"{name}\"?\nLinks from venues to this label will be removed (venues are kept).",
    [Language.KR]: "라벨 \"{name}\"을(를) 삭제할까요?\nVenue에 붙은 라벨만 제거되고 Venue는 유지됩니다.",
  },
  CONFIRM_DELETE_WORKS_BULK: {
    [Language.EN]: "Are you sure you want to delete {count} selected works?\nThe related data (detail cuts, project elements) will also be deleted.\n\nThis action cannot be undone.",
    [Language.KR]: "선택한 Work {count}개를 삭제하시겠습니까?\n관련 데이터(Detail Cut, Project Element)도 삭제됩니다.\n\n이 작업은 취소할 수 없습니다.",
  },
  CONFIRM_DELETE_VENUES_BULK: {
    [Language.EN]: "Delete {count} selected venues?\nThis action cannot be undone.",
    [Language.KR]: "선택한 Venue {count}개를 삭제하시겠습니까?\n이 작업은 취소할 수 없습니다.",
  },
  CONFIRM_DELETE_TEXTS_BULK: {
    [Language.EN]: "Are you sure you want to delete {count} selected texts?\nThe related data (text blocks, project elements) will also be deleted.\n\nThis action cannot be undone.",
    [Language.KR]: "선택한 Text {count}개를 삭제하시겠습니까?\n관련 데이터(Text Block, Project Element)도 삭제됩니다.\n\n이 작업은 취소할 수 없습니다.",
  },
  VIDEO_URL_NULL: {
    [Language.EN]: "Video is not set.",
    [Language.KR]: "영상 URL이 없습니다.",
  },
  INVALID_VIDEO_URL_FORMAT: {
    [Language.EN]: "Invalid video URL format. \nOnly YouTube and Vimeo links are supported.",
    [Language.KR]: "영상 URL 형식이 올바르지 않습니다.\nYouTube 또는 Vimeo 링크만 지원합니다.",
  },
  INVALID_PROJECT_INFO_URL_FORMAT: {
    [Language.EN]: "Invalid link.\nOnly Architrave, Instagram, Behance, and Vimeo links are supported.",
    [Language.KR]: "링크 형식이 올바르지 않습니다.\nArchitrave, Instagram, Behance, Vimeo 링크만 사용할 수 있습니다.",
  },
  INVALID_URL_FORMAT: {
    [Language.EN]: "Invalid URL format.",
    [Language.KR]: "URL 형식이 올바르지 않습니다.",
  },
  CONFIRM_MOVE_TO_VIDEO_URL_PLATFORM: {
    [Language.EN]: "Opening the original video on {platform} in a new tab.",
    [Language.KR]: "{platform}의 원본 영상으로 이동합니다.",
  },
  CONFIRM_MOVE_TO_PDF: {
    [Language.EN]: "Opening the artist's PDF in a new tab.",
    [Language.KR]: "작가가 업로드한 PDF를 새 탭에서 엽니다.",
  },
  CONFIRM_MOVE_TO_TEXT_BLOCK_LINK: {
    [Language.EN]: "Opening the linked Google Drive file in a new tab.",
    [Language.KR]: "연결된 Google Drive 파일을 새 탭에서 엽니다.",
  },
  INVALID_TEXT_BLOCK_LINK_URL_FORMAT: {
    [Language.EN]: "Invalid link.\nOnly Google Drive file links are supported.",
    [Language.KR]: "링크 형식이 올바르지 않습니다.\nGoogle Drive 파일 링크만 사용할 수 있습니다.",
  },
  CONFIRM_MOVE_TO_CAREER_RELATED_URL: {
    [Language.EN]: "Opening the artist's set link in a new tab.",
    [Language.KR]: "작가가 설정한 외부 링크를 새 탭에서 엽니다.",
  },
  CONFIRM_DELETE_DETAIL_CUT: {
    [Language.EN]: "Are you sure you want to delete this detail cut?\nThe related data (Detail cuts in project elements) will also be deleted.\n\nThis action cannot be undone.",
    [Language.KR]: "해당 Detail Cut를 삭제하시겠습니까?\n관련 데이터(Project Element의 Detail Cut)도 삭제됩니다.\n\n이 작업은 취소할 수 없습니다.",
  },
  CONFIRM_DELETE_TEXT_BLOCK: {
    [Language.EN]: "Are you sure you want to delete this text block?\nThe related data (text blocks in project elements) will also be deleted.\n\nThis action cannot be undone.",
    [Language.KR]: "해당 Text Block를 삭제하시겠습니까?\n관련 데이터(Project Element의 Text Block)도 삭제됩니다.\n\n이 작업은 취소할 수 없습니다.",
  },
  TEXTBLOCK_IMAGE_MAX_EXCEEDED: {
    [Language.EN]: 'You can have up to {maxCount} image blocks per text.',
    [Language.KR]: '텍스트 내 이미지는 최대 {maxCount}개까지 설정할 수 있습니다.',
  },
  CONFIRM_DELETE_PROJECT: {
    [Language.EN]: "Are you sure you want to delete this project?\nThe related data (especially project information, visual aids, descriptions) will also be deleted.\n\nThis action cannot be undone.",
    [Language.KR]: "해당 Project를 삭제하시겠습니까?\n관련 데이터(Project Info, Visual Aid, Description 등)도 삭제됩니다.\n\n이 작업은 취소할 수 없습니다.",
  },
  CONFIRM_MAKE_PROJECT_PUBLIC: {
    [Language.EN]: "Would you like to make this project public?\nVisitors will be able to view it.",
    [Language.KR]: "해당 Project를 Public으로 전환할까요??\n모든 사용자가 볼 수 있습니다.",
  },
  CONFIRM_MAKE_PROJECT_PRIVATE: {
    [Language.EN]: "Would you like to make this project private?\nVisitors will no longer be able to view it.",
    [Language.KR]: "해당 Project를 Private으로 전환할까요??\n나만 볼 수 있습니다.",
  },
  INVALID_YEAR_FORMAT: {
    [Language.EN]: "Invalid YEAR format.",
    [Language.KR]: "연도 형식이 올바르지 않습니다.",
  },
  INVALID_MONTH_FORMAT: {
    [Language.EN]: "Month must be between 1 and 12.",
    [Language.KR]: "1월과 12월 사이의 값을 입력해 주세요.",
  },
  INVALID_DAY_FORMAT: {
    [Language.EN]: "Day must be between 1 and 31.",
    [Language.KR]: "1일과 31일 사이의 값을 입력해 주세요.",
  },
  FILE_TOO_BIG_PREVIEW: {
    [Language.EN]: "File is too big. MAX is {maxSize} in preview",
    [Language.KR]: "파일이 너무 큽니다. 최대 크기는 {maxSize}입니다.",
  },
  UNSUPPORTED_IMAGE_FORMAT: {
    [Language.EN]: "Only JPG, JPEG, PNG, or WEBP images are supported.",
    [Language.KR]: "JPG, JPEG, PNG, WEBP 이미지만 사용할 수 있습니다.",
  },
  FAILED_TO_LOAD_IMAGE: {
    [Language.EN]: "Failed to load image",
    [Language.KR]: "이미지를 불러오지 못했습니다.",
  },
  BULK_UPLOAD_IMAGE_MAX_EXCEEDED: {
    [Language.EN]: "Up to {maxCount} images at once.",
    [Language.KR]: "한 번에 최대 {maxCount}개의 이미지를 업로드할 수 있습니다.",
  },
  BULK_IMPORT_MAX_EXCEEDED: {
    [Language.EN]: "Up to {maxCount} items may be selected for import.",
    [Language.KR]: "한 번에 최대 {maxCount}개까지 불러올 수 있습니다.",
  },
  SELECT_TO_IMPORT_EMPTY: {
    [Language.EN]: "Please select at least one Work or Detail to import.",
    [Language.KR]: "가져올 Work 또는 Detail을 선택해주세요.",
  },
  WORK_LIST_EXPORT_MAX_ITEMS: {
    [Language.EN]: 'You can export up to {maxCount} works at once.',
    [Language.KR]: '한 번에 최대 {maxCount}개까지만 내보낼 수 있습니다.',
  },
  SELECT_TO_WORKS_EMPTY: {
    [Language.EN]: "Select works to export.",
    [Language.KR]: "추가할 작품을 선택해주세요.",
  },
  FORMATION_DISPLAY_SIZE_NOT_AVAILABLE: {
    [Language.EN]: "Only WIDE, BIG, and MEDIUM are available for Formation.",
    [Language.KR]: "Formation에서는 WIDE, BIG, MEDIUM만 선택할 수 있습니다.",
  },
  ADD_WORK_BEFORE_FLOOR_PLAN: {
    [Language.EN]: "Add works to the project first.",
    [Language.KR]: "프로젝트에 작품을 먼저 추가해주세요.",
  },
  ONLY_NUMBERS_ALLOWED: {
    [Language.EN]: "Only numbers are allowed.",
    [Language.KR]: "숫자만 입력 가능합니다.",
  },
  INVALID_SIZE_FORMAT: {
    [Language.EN]: "Invalid Size format.\nOnly positive numbers, including 0, with up to one decimal place are allowed.",
    [Language.KR]: "잘못된 크기 형식입니다.\n0을 포함한 양수만 입력 가능하며, 소수점 이하 한 자리까지입니다.",
  },
  SET_MAIN_SIZE_UNIT_REQUIRES_SIZE: {
    [Language.EN]: "Enter a size value first.",
    [Language.KR]: "크기 값을 먼저 입력하세요.",
  },
  SET_MAIN_SIZE_UNIT_REQUIRES_SIZE_FOR_ALL: {
    [Language.EN]: "All selected works need this size value.",
    [Language.KR]: "선택된 모든 작품에 해당 단위 값이 필요합니다.",
  },
  INVALID_WORK_TYPE: {
    [Language.EN]: "Invalid work type.",
    [Language.KR]: "작품 유형이 올바르지 않습니다.",
  },
  COUNTRY_NOT_SUPPORTED: {
    [Language.EN]: "Country not supported yet.",
    [Language.KR]: "아직 지원하지 않는 국가입니다.",
  },
  INVALID_PRICE_FORMAT: {
    [Language.EN]: "Invalid price format. Only positive integers are allowed.",
    [Language.KR]: "가격 형식이 올바르지 않습니다. 0 이상의 숫자만 입력할 수 있습니다.",
  },
  USER_NOT_FOUND: {
    [Language.EN]: "User not found. Check the username and try again.",
    [Language.KR]: "사용자를 찾을 수 없습니다.",
  },
  INVALID_EMAIL_FORMAT: {
    [Language.EN]: "Invalid email format.",
    [Language.KR]: "이메일 형식이 올바르지 않습니다.",
  },
  CONFIRM_DELETE_CAREER: {
    [Language.EN]: "Are you sure you want to delete this career?",
    [Language.KR]: "이 경력을 삭제하시겠습니까?",
  },
  THANK_YOU_PARTICIPATION: {
    [Language.EN]: "Thank you for your participation.",
    [Language.KR]: "참여해 주셔서 감사합니다.",
  },
  SUBSCRIBE_USE_DESKTOP: {
    [Language.EN]: "To subscribe, please use the desktop.",
    [Language.KR]: "구독하려면 Desktop 환경에서 이용해주세요.",
  },
  CONFIRM_CHANGE_LINK_ID: {
    [Language.EN]: "Once changed, the current Link ID will no longer work.\nProceed with the change?",
    [Language.KR]: "변경하면 현재 Link ID가 더 이상 작동하지 않습니다.\n변경을 진행하시겠습니까?",
  },
  CONFIRM_LOGOUT_UNSAVED: {
    [Language.EN]: "You have unsaved changes. Are you sure you want to log out?\nAny unsaved changes will be discarded.",
    [Language.KR]: "저장되지 않은 변경사항이 있습니다. 로그아웃하시겠습니까?\n저장되지 않은 모든 변경사항은 삭제됩니다.",
  },
  CONFIRM_LOGOUT: {
    [Language.EN]: "Do you want to log out?",
    [Language.KR]: "로그아웃하시겠습니까?",
  },
  CHECK_FORMAT: {
    [Language.EN]: "Please check the format.",
    [Language.KR]: "올바른 형식인지 확인해주세요.",
  },
  PREVIEW_MODE_VERIFY_EMAIL: {
    [Language.EN]: "You're in preview mode.\nVerify your email to unlock the full experience.",
    [Language.KR]: "현재 미리보기 단계입니다.\n전체 기능을 사용하려면 이메일을 인증해주세요.",
  },
  CONFIRM_GO_INSTAGRAM: {
    [Language.EN]: "Would you like to go to our Instagram page?",
    [Language.KR]: "인스타그램 @architrave.work에서 소식을 확인합니다.",
  },
  CONFIRM_DELETE_SUPPORT_MESSAGE: {
    [Language.EN]: "Are you sure you want to delete this support message?",
    [Language.KR]: "이 문의 내용을 삭제하시겠습니까?",
  },
  CONFIRM_DELETE_MATERIAL_ALIAS: {
    [Language.EN]:
      "Delete material alias \"{keyword}\"?\nThe alias set and existing works will be kept.\nThis alias may be created again when the material is detected.",
    [Language.KR]:
      "Material Alias \"{keyword}\"을(를) 삭제하시겠습니까?\nAlias Set과 기존 작품은 유지됩니다.\n같은 재료가 다시 감지되면 Alias가 재생성될 수 있습니다.",
  },
  CONFIRM_DELETE_VENUE: {
    [Language.EN]:
      "Delete venue \"{name}\"?\nThis cannot be undone.\nPermanently closed venues should be set to PERMANENTLY_CLOSED instead.",
    [Language.KR]:
      "Venue \"{name}\"을(를) 삭제하시겠습니까?\n이 작업은 취소할 수 없습니다.\n폐관한 공간은 삭제 대신 상태를 PERMANENTLY_CLOSED로 바꿔 주세요.",
  },
  SUPPORT_TYPE_REQUIRED: {
    [Language.EN]: "Select an inquiry type.",
    [Language.KR]: "문의 유형을 선택해 주세요.",
  },
  SUPPORT_AREA_REQUIRED: {
    [Language.EN]: "Please select a related page.",
    [Language.KR]: "관련 페이지를 선택해 주세요.",
  },
  SUPPORT_CONTENT_REQUIRED: {
    [Language.EN]: "Please enter a title and message.",
    [Language.KR]: "제목과 내용을 입력해 주세요.",
  },
  CONFIRM_MAKE_PAGE_PUBLIC: {
    [Language.EN]:
      "Do you want to make your page public?\nUsername search will be enabled, and Archive Index will be listed.",
    [Language.KR]:
      "해당 Archive를 Public 상태로 변경하시겠습니까?\n Username 검색과 Archive Index 목록에 올라갑니다.",
  },
  CONFIRM_MAKE_PAGE_SEARCHABLE: {
    [Language.EN]:
      "Do you want to make your page searchable?\nUsername search will be enabled, but Archive Index will not be listed.",
    [Language.KR]:
      "해당 Archive를 Searchable 상태로 변경하시겠습니까?\n Username 검색은 가능하나 Archive Index 목록에는 올라가지 않습니다.",
  },
  CONFIRM_MAKE_PAGE_PRIVATE: {
    [Language.EN]:
      "Do you want to make your page private?\nUsername search will be disabled, and Archive Index will not be listed.",
    [Language.KR]:
      "해당 Archive를 Private 상태로 변경하시겠습니까?\n Username 검색과 Archive Index 목록에 올라가지 않습니다.",
  },
  // Password validation (useValidation explainPasswordIssues)
  PASSWORD_LENGTH_REQUIRED: {
    [Language.EN]: "8-20 characters required.",
    [Language.KR]: "8-20자로 입력해주세요.",
  },
  PASSWORD_NEED_LOWERCASE: {
    [Language.EN]: "Add a lowercase letter.",
    [Language.KR]: "소문자를 포함해주세요.",
  },
  PASSWORD_NEED_UPPERCASE: {
    [Language.EN]: "Add an uppercase letter.",
    [Language.KR]: "대문자를 포함해주세요.",
  },
  PASSWORD_NEED_NUMBER: {
    [Language.EN]: "Add a number.",
    [Language.KR]: "숫자를 포함해주세요.",
  },
  PASSWORD_NEED_SPECIAL: {
    [Language.EN]: "Add a special character (e.g. !@#$%).",
    [Language.KR]: "특수문자를 포함해주세요 (예: !@#$%).",
  },
  PASSWORD_NO_SPACES: {
    [Language.EN]: "No spaces allowed.",
    [Language.KR]: "공백은 사용할 수 없습니다.",
  },
};

/**
 * Alert 메시지를 가져오는 함수
 * 언어는 languageStore에서 자동으로 가져옵니다.
 * @param key 메시지 키
 * @param params 메시지에 포함될 파라미터 (예: {maxLength: 100})
 * @returns 번역된 메시지
 */
export function getAlertMessage(
  key: AlertMessageKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = alertMessages[key];
  
  if (!message) {
    console.warn(`Alert message key "${key}" not found.`);
    return key;
  }
  
  // 선택한 언어가 없으면 영어로 폴백
  let translatedMessage = message[language] || message[Language.EN];
  
  // 파라미터 치환 (예: {maxLength} → 실제 숫자)
  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      const safeValue = paramValue !== undefined && paramValue !== null
        ? String(paramValue)
        : '';
      const pattern = new RegExp(`\\{${paramKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`, 'g');
      translatedMessage = translatedMessage.replace(pattern, safeValue);
    });
  }
  
  return translatedMessage;
}
